/**
 * Floating Presence Button
 * Global access to "Enter Presence" - the Midday Interrupt
 * 
 * Appears throughout the app to interrupt scrolling habits
 * Triggers the Entry Transition before opening the Midday Reset
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Leaf } from 'lucide-react';
import EntryTransition from './EntryTransition';

const FloatingPresenceButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [showTransition, setShowTransition] = useState(false);

  // Hide on presence pages + public share pages
  const hideOnRoutes = [
    "/golf/coach",
    "/soulumo/from",
    "/soulumo/coach",
    "/atelier/guide",
    "/gym/guide",
    "/networker/guide",
    "/foundations/guide",
    "/lights/guide",
    "/soulumo/guide",
    "/inner-path/guide",
    '/reclaim-presence',
    '/presence-journey',
    '/abundance/living-vision-board',
    '/healing',
    '/journeys',
    '/gift-moment',
    '/share/audio',
    '/gift/',
    '/listen',
    '/living-dream',
    '/atelier',
    '/gift/atelier',
    '/share/alignment',
    '/team-alignment',
    '/rewrite',
    '/golf/reclaim-the-shot',
    '/clearing/reclaim-the-pattern',
    '/abundance/rewrite-scarcity',
    '/presence/return-to-here',
    '/relationships/rewrite-the-armour',
    '/performance/free-under-pressure',
    '/inner-path/deep/',
    '/inner-path/daily/',
    '/inner-path/morning-arrival',
    '/heart-ru',
    '/spine/',
    '/heart/',
  ];
  
  if (hideOnRoutes.some(route => location.pathname.startsWith(route))) {
    return null;
  }

  const handleClick = () => {
    // Trigger Entry Transition first
    setShowTransition(true);
  };

  const handleTransitionComplete = () => {
    setShowTransition(false);
    // Navigate to Midday Interrupt (quick reset)
    navigate('/reclaim-presence/midday');
  };

  return (
    <>
      {/* Entry Transition Overlay */}
      <EntryTransition 
        isActive={showTransition} 
        onComplete={handleTransitionComplete}
        duration={2000}
      />

      {/* Floating Button - Positioned below the reset button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.5, type: 'spring', stiffness: 200 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={handleClick}
        className="fixed top-36 right-3 z-40 w-9 h-9 rounded-full bg-gradient-to-br from-emerald-600/90 to-teal-700/90 shadow-lg shadow-emerald-500/30 flex items-center justify-center text-white hover:shadow-xl hover:shadow-emerald-500/40 transition-shadow"
        data-testid="floating-presence-btn"
        aria-label="Enter Presence"
      >
        <motion.div
          animate={{ 
            scale: isHovered ? 1.1 : 1,
            rotate: isHovered ? 10 : 0 
          }}
          transition={{ duration: 0.3 }}
        >
          <Leaf size={16} strokeWidth={2.5} />
        </motion.div>
      </motion.button>

      {/* Tooltip on hover */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="fixed top-[148px] right-[48px] z-40 bg-[#0A1A0A]/90 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap backdrop-blur-sm border border-emerald-800/30"
          >
            Enter Presence
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingPresenceButton;
